import React, { useEffect, useState } from 'react';
import { apiClient, useAuth } from '../context/AuthContext';

// ============================================================================
// PagoVencidoScreen — pantalla que ve un usuario NO admin cuando su pago
// está vencido. Le explica la situación y le permite:
//   - Ver su historial de pagos
//   - Cerrar sesión
// Bloquea el acceso al resto de la app hasta que el admin registre un pago.
// ============================================================================

const formatearFecha = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
};

const formatearMoneda = (n) => {
  if (n === null || n === undefined) return '—';
  return '$' + Number(n).toLocaleString('es-CO', { maximumFractionDigits: 0 });
};

const PagoVencidoScreen = ({ estado }) => {
  const { user, logout } = useAuth();
  const [pagos, setPagos] = useState([]);
  const [cargandoPagos, setCargandoPagos] = useState(false);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const cargarHistorico = async () => {
    if (pagos.length > 0) { setMostrarHistorico(true); return; }
    setCargandoPagos(true);
    try {
      const resp = await apiClient.get('/mis-pagos');
      setPagos(resp.data?.pagos || []);
      setMostrarHistorico(true);
    } catch (error) {
      // Si falla, mostrar igual la pantalla
      setMostrarHistorico(true);
    } finally {
      setCargandoPagos(false);
    }
  };

  const esPruebaVencida = estado?.estado === 'prueba_vencida';

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 my-8">
        {/* Icono */}
        <div className="text-center mb-6">
          <div className="text-6xl mb-2">{esPruebaVencida ? '⏰' : '🔒'}</div>
          <h1 className="text-2xl font-bold text-red-700 mb-1">
            {esPruebaVencida ? 'Tu periodo de prueba finalizó' : 'Cuenta deshabilitada'}
          </h1>
          <p className="text-gray-600">
            {esPruebaVencida
              ? '¿Te gustó la app? Contacta al administrador para registrar tu primer pago y seguir usándola.'
              : 'Por favor contacta al administrador para regularizar tu pago.'}
          </p>
        </div>

        {/* Detalle del usuario */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-red-600 uppercase font-medium">Usuario</div>
              <div className="font-semibold text-gray-800">{user?.nombre_completo}</div>
              <div className="text-xs text-gray-600">{user?.email}</div>
            </div>
            <div>
              <div className="text-xs text-red-600 uppercase font-medium">Estado</div>
              <div className="font-bold text-red-700">
                {esPruebaVencida ? '⏰ Prueba vencida' : '🔴 Pago vencido'}
              </div>
              {estado?.vigencia_hasta && (
                <div className="text-xs text-gray-600">
                  {esPruebaVencida ? 'Tu prueba' : 'La vigencia'} terminó el {formatearFecha(estado.vigencia_hasta)}
                  {estado.dias_restantes !== null && estado.dias_restantes !== undefined && (
                    <> ({Math.abs(estado.dias_restantes)} día(s) hace)</>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Instrucciones */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5 text-sm text-blue-900">
          <strong>¿Qué hacer?</strong>
          <ul className="list-disc list-inside mt-1 space-y-0.5 text-blue-800">
            <li>Contacta al administrador para coordinar el pago.</li>
            <li>Una vez registrado el pago, podrás volver a usar la app inmediatamente.</li>
            <li>Tus datos (cuentas, evidencias, configuración) están a salvo: no se pierden.</li>
          </ul>
        </div>

        {/* Botón ver histórico */}
        {!mostrarHistorico ? (
          <button
            onClick={cargarHistorico}
            disabled={cargandoPagos}
            className="w-full mb-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm border border-gray-300 disabled:opacity-50"
          >
            {cargandoPagos ? 'Cargando...' : '📜 Ver mi histórico de pagos'}
          </button>
        ) : (
          <div className="mb-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <strong className="text-sm text-gray-800">Mi histórico ({pagos.length})</strong>
              <button onClick={() => setMostrarHistorico(false)} className="text-xs text-gray-500 hover:text-gray-700">Ocultar</button>
            </div>
            {pagos.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No hay pagos registrados aún para tu cuenta.</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {pagos.map(p => (
                  <div key={p.id} className="text-xs bg-white border rounded p-2">
                    <div className="font-bold text-emerald-700">{formatearMoneda(p.monto)}</div>
                    <div className="text-gray-600">
                      Periodo: {formatearFecha(p.periodo_inicio)} → {formatearFecha(p.periodo_fin)}
                    </div>
                    <div className="text-gray-500">
                      Pagado el {formatearFecha(p.fecha_pago)}
                      {p.metodo_pago && <> · {p.metodo_pago}</>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Cerrar sesión */}
        <button
          onClick={logout}
          className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium"
        >
          Cerrar sesión
        </button>

        <p className="text-center text-[11px] text-gray-400 mt-4">
          Esta pantalla aparecerá hasta que el administrador registre un pago nuevo en tu cuenta.
        </p>
      </div>
    </div>
  );
};

export default PagoVencidoScreen;
